import { Injectable } from '@angular/core';
import {
  downloadingFileByUrlStarted,
  exportOrdersAsyncSuccessLabel,
  exportOrdersFailureLabel,
  exportOrdersSyncSuccessLabel,
} from '@epicuro-next/constants/features/localization';
import { EXPORT_XLS_FILE_DEFAULT_NAME } from '@epicuro-next/constants/features/order-book';
import { OrdersService, OrdersV2Service } from '@epicuro-next/data/order-book';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { getFileNameFromUrl } from '@epicuro-next/utilities';
import { downloadFile } from '@epicuro-next/utilities-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { EMPTY, forkJoin, of, tap } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  clearFilterById,
  selectExportOrderBookV2Request,
  selectOrderFiltersQuery,
  setActiveFilters,
  setInitialSelectedFiltersFromUrlSuccess,
  setSearchQuery,
} from '../../filters';
import * as fromActions from '../orders.actions';
import { selectSelectedOrders } from '../orders.selectors';

@Injectable()
export class OrdersEffects {
  constructor(
    private actions$: Actions,
    private orders: OrdersService,
    private store: Store,
    private ordersV2Service: OrdersV2Service,
  ) {}

  public loadOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadOrders),
      concatLatestFrom(() => this.store.pipe(select(selectOrderFiltersQuery))),
      switchMap(([_, query]) => {
        return this.orders.getOrders(query).pipe(
          map(({ orders, totals, afterKey }) =>
            fromActions.loadOrdersSuccess({ orders, totals, afterKey }),
          ),
          catchError((error) =>
            of(fromActions.loadOrdersFailure({ error: error.message })),
          ),
        );
      }),
    ),
  );

  public loadNextPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.increaseOrderBookPageNumber),
      concatLatestFrom(() => this.store.pipe(select(selectOrderFiltersQuery))),
      switchMap(([_, query]) => {
        return this.orders.getOrders(query).pipe(
          map(({ orders, afterKey }) =>
            fromActions.increasePageNumberSuccess({
              orders,
              afterKey,
            }),
          ),
          catchError(this.handlePaginationError),
        );
      }),
    ),
  );

  public loadOrdersFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        setActiveFilters,
        setSearchQuery,
        clearFilterById,
        setInitialSelectedFiltersFromUrlSuccess,
      ),
      map(() => fromActions.loadOrders()),
    ),
  );

  public exportOrdersV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.exportOrders, fromActions.exportAllOrders),
      concatLatestFrom(() => [
        this.store.pipe(select(selectExportOrderBookV2Request)),
        this.store.pipe(select(selectSelectedOrders)),
      ]),
      map(([action, exportRequest, selectedOrders]) => ({
        ...exportRequest,
        actionType: action.type,
        orderNumbers: (selectedOrders ?? []).map(
          ({ salesOrderNumber }) => salesOrderNumber,
        ),
      })),
      switchMap(({ actionType, ...request }) => {
        return this.ordersV2Service.exportToXls(request).pipe(
          switchMap((response) => {
            if (response.status === 202) {
              return of([exportOrdersAsyncSuccessLabel]);
            }

            if (response.status === 200) {
              return forkJoin([
                of(exportOrdersSyncSuccessLabel),
                this.downloadOrdersByUrl(response.body as string),
              ]);
            }

            return EMPTY;
          }),
          switchMap(([message]) => [
            showSnackBarMessage({
              message,
              messageType: MessageType.SUCCESS,
            }),
            actionType === fromActions.exportOrders.type
              ? fromActions.exportOrdersSuccess()
              : fromActions.exportAllOrdersSuccess(),
          ]),
          catchError((error) => [
            showSnackBarMessage({
              message: exportOrdersFailureLabel,
              messageType: MessageType.DANGER,
            }),
            actionType === fromActions.exportOrders.type
              ? fromActions.exportOrdersFailure({ error: error.message })
              : fromActions.exportAllOrdersFailure({
                  error: error.message,
                }),
          ]),
        );
      }),
    ),
  );

  private downloadOrdersByUrl(url: string) {
    this.store.dispatch(
      showSnackBarMessage({
        messageType: MessageType.SUCCESS,
        message: downloadingFileByUrlStarted,
      }),
    );
    return this.ordersV2Service.downloadFileByUrl(url).pipe(
      tap((responseBlob) => {
        const fileName =
          getFileNameFromUrl(url) ?? EXPORT_XLS_FILE_DEFAULT_NAME;
        downloadFile(responseBlob.body as Blob, fileName);
      }),
    );
  }

  private handlePaginationError(error: Error) {
    return of(
      fromActions.increasePageNumberFailure({
        error: error.message,
      }),
    );
  }
}
